import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

import { Bar } from 'react-chartjs-2';

import styles from './index.module.scss';
import { Box, Stack, Typography } from '@mui/material';
import Wysiwyg from '../Wysiwyg';
import { insightsChartPlotSurfaceSx, pricingReportChartTitleSx } from '../../../routes/pricing-reports/constants';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Merge optional Chart.js user options into defaults (nested `plugins`, `scales`, axis `ticks`).
 * No new dependency — callers pass small overrides only.
 */
function mergeChartJsUserOptions(base = {}, override = {}) {
    if (!isPlainObject(override) || Object.keys(override).length === 0) {
        return { ...base };
    }
    const merged = { ...base, ...override };

    if (isPlainObject(base.plugins) || isPlainObject(override.plugins)) {
        merged.plugins = { ...base.plugins, ...override.plugins };
        const baseTooltip = base.plugins?.tooltip;
        const overrideTooltip = override.plugins?.tooltip;
        if (isPlainObject(baseTooltip) || isPlainObject(overrideTooltip)) {
            merged.plugins.tooltip = { ...baseTooltip, ...overrideTooltip };
            if (isPlainObject(baseTooltip?.callbacks) || isPlainObject(overrideTooltip?.callbacks)) {
                merged.plugins.tooltip.callbacks = {
                    ...baseTooltip?.callbacks,
                    ...overrideTooltip?.callbacks,
                };
            }
        }
    }

    if (isPlainObject(base.scales) || isPlainObject(override.scales)) {
        merged.scales = { ...base.scales, ...override.scales };
        const axisKeys = new Set([
            ...Object.keys(base.scales || {}),
            ...Object.keys(override.scales || {}),
        ]);
        axisKeys.forEach((axis) => {
            const b = base.scales?.[axis];
            const o = override.scales?.[axis];
            if (!isPlainObject(b) && !isPlainObject(o)) return;
            merged.scales[axis] = { ...b, ...o };
            if (isPlainObject(b?.ticks) || isPlainObject(o?.ticks)) {
                merged.scales[axis].ticks = { ...b?.ticks, ...o?.ticks };
            }
            if (isPlainObject(b?.title) || isPlainObject(o?.title)) {
                merged.scales[axis].title = { ...b?.title, ...o?.title };
            }
        });
    }

    return merged;
}

export const options = {
    responsive: true,
    plugins: {
        legend: {
            display: false,
        },
        title: {
            display: true,
        },
    },
};

const DEFAULT_CHART_PLOT_HEIGHT_PX = 320;

function ChartContainer({
    title,
    description,
    footerAction,
    labels = [],
    datasets = [],
    chartOptions = null,
    chartRef,
    chartPlotHeight = DEFAULT_CHART_PLOT_HEIGHT_PX,
    pricingReportHeadings = false,
}) {
    const defaultOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
            },
        },
    };

    const options = mergeChartJsUserOptions(defaultOptions, chartOptions);

    const data = {
        labels,
        datasets,
    };

    return (
        <Box sx={{ p: 2, ...insightsChartPlotSurfaceSx }}>
            <Stack
                spacing={2}
                sx={{
                    width: '100%',
                    mb: pricingReportHeadings ? 1.5 : 2,
                }}
            >
                <Stack
                    sx={{
                        width: '100%',
                    }}
                >
                    {!!title &&
                        <Typography sx={pricingReportHeadings ? pricingReportChartTitleSx : {
                            fontWeight: 'bold',
                            color: '#090909',
                            fontSize: '0.9rem',
                            textTransform: 'capitalize',
                        }}>
                            {pricingReportHeadings ? title : (
                                <Wysiwyg truncateAfterLine={2}>
                                    {title}
                                </Wysiwyg>
                            )}
                        </Typography>
                    }

                    {!!description &&
                        <Typography
                            variant={pricingReportHeadings ? "body2" : undefined}
                            color={pricingReportHeadings ? "text.secondary" : undefined}
                            sx={pricingReportHeadings ? { mt: 0.5 } : {
                            fontWeight: 300,
                            color: '#090909',
                            fontSize: '0.8rem'
                        }}>
                            {pricingReportHeadings ? description : (
                                <Wysiwyg truncateAfterLine={2}>
                                    {description}
                                </Wysiwyg>
                            )}
                        </Typography>
                    }
                </Stack>

                <Box
                    className={styles.chartContainer}
                    {...(chartRef ? { "data-pricing-chart-plot": "" } : {})}
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: chartPlotHeight,
                    }}
                >
                    <Bar ref={chartRef} options={options} data={data} />
                </Box>
            </Stack>

            {
                footerAction &&
                <Box
                    sx={{
                    position: 'absolute',
                    bottom: '0',
                    left: '16px',
                    right: '16px',
                    }}
                >
                    {footerAction}
                </Box>
            }
        </Box>
    )
}

// ChartContainer.propTypes = {
//     title: string.isRequired(),
//     description: string,
//     labels: arrayOf(string),
//     labels: arrayOf(
//         shape({
//             label: string.isRequired(),
//             data: arrayOf(number).isRequired(),
//             backgroundColor: string.isRequired(),
//         })
//     ),
// }

export default ChartContainer